import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { addDays, addHours, addWeeks, format, parseISO } from 'date-fns';

import {
  calendarEventCreate,
  calendarEventDelete,
  calendarEventUpdate,
  calendarWeek,
} from '../../../api/calendar';
import CalendarEventModal from './CalendarEventModal';
import {
  API_DATETIME,
  dayKeyOf,
  dayLabel,
  daysWithEvents,
  hoursRange,
  indexEvents,
  isSameDate,
  isWeekend,
  shortWeekday,
  toApiDay,
  TYPE_MODIFIER,
  readErrorText,
  weekDays,
  weekLabel,
  weekStartOf,
} from './constants';
import './calendar-panel.style.scss';

const DESKTOP_QUERY = '(min-width: 768px)';

/** Має збігатися з `$cal-close-ms` у SCSS. */
const CLOSE_ANIMATION_MS = 340;

/**
 * Каскад появи колонок. Значення дзеркалять `$cal-cascade-base`/`$cal-cascade-step`
 * у SCSS: шкала доби має починати рости рівно тоді, коли ЇЇ колонка вже проявилась.
 */
const CASCADE_BASE_MS = 230;

const CASCADE_STEP_MS = 46;

const CASCADE_TAIL_MS = 220;

/** Скільки летить «плашка» нової події. Дзеркалить `transition` у `.cal-flyer`. */
const FLIGHT_MS = 560;

/** Скільки нова подія лишається підсвіченою після прильоту. */
/** Дзеркалить `cal-chip-land` + `cal-chip-glow` у SCSS (420 + 1800 мс). */
const HIGHLIGHT_MS = 2220;

const TOAST_MS = 3200;

const prefersReducedMotion = () => (
  typeof window !== 'undefined'
  && typeof window.matchMedia === 'function'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches
);

const EMPTY_WEEK = {
  connected: false,
  available: false,
  enabled: false,
  events: [],
  reminder_default: 60,
};

/**
 * Панель «Мій календар»: тижнева сітка власних подій Google Calendar
 * + read-only заняття, пробні й відпрацювання з розкладу.
 *
 * Стан локальний (без Redux) — так само, як у `GoogleCalendarCard` і
 * `Certificates`: спільні барелі store не чіпаємо.
 */
const CalendarPanel = ({ token, origin, onClose }) => {
  const navigate = useNavigate();
  const [weekStart, setWeekStart] = useState(() => weekStartOf(new Date()));
  const [selectedDay, setSelectedDay] = useState(() => new Date());
  const [week, setWeek] = useState(EMPTY_WEEK);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modal, setModal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState('');
  // Підсвітка «хрестом»: колонка дня + рядок години під курсором.
  const [hover, setHover] = useState({ day: null, hour: null });
  // Позиція лінії «зараз». Оновлюється раз на хвилину — інакше після довгого
  // відкритого вікна лінія показувала б час відкриття.
  const [now, setNow] = useState(() => new Date());
  const [isDesktop, setIsDesktop] = useState(
    () => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(DESKTOP_QUERY).matches : true)
  );

  const [closing, setClosing] = useState(false);
  const [toast, setToast] = useState(null);
  const [flyer, setFlyer] = useState(null);
  const [highlightId, setHighlightId] = useState(null);
  const [navPulse, setNavPulse] = useState(null);
  // Шкала доби доростає ПІСЛЯ каскаду колонок, а не разом з ним.
  const [entered, setEntered] = useState(false);
  const boxRef = useRef(null);
  const flyerRef = useRef(null);
  const timersRef = useRef([]);
  // Наскрізний лічильник: кожен тост/політ гасить ТІЛЬКИ себе. Інакше таймер
  // першої події збивав би підсвітку й тост другої, створеної слідом.
  const seqRef = useRef(0);
  const closeTimerRef = useRef(null);

  // Відповіді приходять у довільному порядку — застарілу просто ігноруємо,
  // інакше швидке гортання тижнів лишало б у сітці чужі дані.
  const requestRef = useRef(0);

  /** Усі відкладені ефекти тримаємо в одному місці — щоб знімати їх на unmount. */
  const later = useCallback((fn, delay) => {
    const timer = setTimeout(fn, delay);

    timersRef.current.push(timer);

    return timer;
  }, []);

  useEffect(() => () => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  }, []);

  const showToast = useCallback((text, tone = 'ok') => {
    seqRef.current += 1;

    const id = seqRef.current;

    setToast({ text, tone, id });
    later(() => setToast((current) => (current && current.id === id ? null : current)), TOAST_MS);
  }, [later]);

  /**
   * `silent` — оновлення без скелетона й без скидання стану сітки. Саме через
   * нього календар більше не «блимає» після створення чи видалення події:
   * `loading` не смикається, ключі клітинок ті самі, тож React нічого не
   * перемонтовує і каскад не програється заново.
   */
  const load = useCallback((start, { silent = false } = {}) => {
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;

    if (!silent) {
      setLoading(true);
      setError('');
    }

    calendarWeek({ token, start: toApiDay(start) })
      .then((response) => {
        if (requestRef.current !== requestId) {
          return;
        }

        if (response?.data?.info?.status) {
          const data = response.data.data.calendar;

          setWeek({ ...EMPTY_WEEK, ...data });

          // Сервер тримає тиждень у межах ±2 років; якщо він його затиснув —
          // підтягуємо підпис і сітку, інакше вони показували б чужий діапазон.
          if (data?.week?.start && data.week.start !== toApiDay(start)) {
            setWeekStart(weekStartOf(parseISO(data.week.start)));
          }
        } else if (!silent) {
          setError(response?.data?.info?.message || 'Не вдалося завантажити календар.');
        }
      })
      .catch((requestError) => {
        if (requestRef.current !== requestId) {
          return;
        }

        const text = readErrorText(requestError, 'Не вдалося оновити календар.');

        // Тихе оновлення не має підміняти вже показану сітку екраном помилки —
        // людина щойно щось зробила і хоче бачити результат, а не порожнечу.
        if (silent) {
          showToast(text, 'error');
        } else {
          setError(text);
        }
      })
      .finally(() => {
        // Без прив'язки до `silent`: якщо non-silent запит устиг застаріти,
        // його `finally` — єдиний, хто ще може зняти скелетон.
        if (requestRef.current === requestId) {
          setLoading(false);
        }
      });
  }, [token, showToast]);

  /**
   * Закриття з анімацією: спершу програємо згортання в кнопку, і лише потім
   * розмонтовуємо панель. Прямий `onClose()` прибрав би її миттєво.
   */
  const requestClose = useCallback(() => {
    if (closeTimerRef.current !== null) {
      return;
    }

    if (prefersReducedMotion()) {
      onClose();

      return;
    }

    setClosing(true);
    closeTimerRef.current = setTimeout(onClose, CLOSE_ANIMATION_MS);
  }, [onClose]);

  useEffect(() => () => {
    if (closeTimerRef.current !== null) {
      clearTimeout(closeTimerRef.current);
    }
  }, []);

  /**
   * Точка, з якої панель розгортається — центр кнопки в шапці, переведений у
   * координати самої панелі. Рахуємо в layout-ефекті: до першого кадру, інакше
   * анімація встигла б стартувати з дефолтного правого верхнього кута.
   */
  useLayoutEffect(() => {
    const box = boxRef.current;

    if (!box || !origin) {
      return;
    }

    const rect = box.getBoundingClientRect();

    box.style.setProperty('--cal-origin-x', `${origin.x - rect.left}px`);
    box.style.setProperty('--cal-origin-y', `${origin.y - rect.top}px`);
  }, [origin]);

  useEffect(() => {
    load(weekStart);
  }, [load, weekStart]);

  // Власний клас, а не спільний `open`: його ставлять і знімають ще й
  // NotificationHeader, Modals і FinancesPayBtns — закриття будь-якого з них
  // розблокувало б скрол під відкритою панеллю.
  useEffect(() => {
    document.body.classList.add('cal-open');

    return () => document.body.classList.remove('cal-open');
  }, []);

  useEffect(() => {
    const onKeyDown = (domEvent) => {
      if (domEvent.key !== 'Escape') {
        return;
      }

      // Escape при відкритій формі має закрити ФОРМУ, а не всю панель:
      // інакше введена, але не збережена подія зникає безповоротно.
      if (modal) {
        setModal(null);
        setModalError('');

        return;
      }

      requestClose();
    };

    document.addEventListener('keydown', onKeyDown);

    return () => document.removeEventListener('keydown', onKeyDown);
  }, [modal, requestClose]);

  useEffect(() => {
    if (!window.matchMedia) {
      return undefined;
    }

    const query = window.matchMedia(DESKTOP_QUERY);
    const onChange = (domEvent) => setIsDesktop(domEvent.matches);

    // Safari < 14 не має addEventListener на MediaQueryList.
    if (query.addEventListener) {
      query.addEventListener('change', onChange);

      return () => query.removeEventListener('change', onChange);
    }

    query.addListener(onChange);

    return () => query.removeListener(onChange);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60000);

    return () => clearInterval(timer);
  }, []);

  /**
   * «Політ» щойно створеної події з модалки на її місце в сітці.
   *
   * Ціль шукаємо в такому порядку: клітинка «день × година» → колонка дня
   * (година може бути поза видимим діапазоном або ми на мобільному вигляді) →
   * стрілка тижня, якщо дата взагалі поза показаним тижнем. Стрілка при цьому
   * підсвічується — це єдине, що підказує, у який бік поїхала подія.
   */
  const launchFlight = useCallback((created, fromRect) => {
    if (!created || prefersReducedMotion()) {
      return;
    }

    const root = boxRef.current;

    if (!root || !fromRect) {
      return;
    }

    const weekEndKey = toApiDay(addDays(weekStart, 6));
    const weekStartKey = toApiDay(weekStart);
    const hour = Number(String(created.start || '').slice(0, 2));

    let target = null;
    let direction = null;

    if (created.date < weekStartKey) {
      direction = 'prev';
    } else if (created.date > weekEndKey) {
      direction = 'next';
    }

    if (direction) {
      target = root.querySelector(`[data-nav="${direction}"]`);
    } else {
      target = root.querySelector(`[data-cell="${created.date}|${hour}"]`)
        || root.querySelector(`[data-day="${created.date}"]`);
    }

    if (!target) {
      return;
    }

    const toRect = target.getBoundingClientRect();

    seqRef.current += 1;

    const flightId = seqRef.current;

    setFlyer({
      id: flightId,
      label: created.title,
      left: fromRect.left + (fromRect.width / 2) - 90,
      top: fromRect.top + (fromRect.height / 2) - 18,
      dx: (toRect.left + (toRect.width / 2)) - (fromRect.left + (fromRect.width / 2)),
      dy: (toRect.top + (toRect.height / 2)) - (fromRect.top + (fromRect.height / 2)),
    });

    if (direction) {
      // Об'єкт з id, а не рядок: повторний політ у той самий бік мусить
      // перезапустити анімацію, а незмінний className її не рестартує.
      setNavPulse({ dir: direction, id: flightId });
      later(
        () => setNavPulse((current) => (current && current.id === flightId ? null : current)),
        FLIGHT_MS + 400
      );
    } else if (created.id) {
      // Підсвітку вмикаємо в момент приземлення, а не одразу: інакше вона
      // згасла б ще до того, як плашка долетіла.
      later(() => setHighlightId(created.id), FLIGHT_MS - 120);
      later(
        () => setHighlightId((current) => (current === created.id ? null : current)),
        FLIGHT_MS + HIGHLIGHT_MS
      );
    }

    later(() => setFlyer((current) => (current && current.id === flightId ? null : current)), FLIGHT_MS);
  }, [weekStart, later]);

  useEffect(() => {
    const node = flyerRef.current;

    if (!flyer || !node) {
      return undefined;
    }

    // Перший кадр — стартова точка, другий — кінцева: без цього браузер
    // застосував би обидва стани разом і transition не відпрацював би.
    const frame = requestAnimationFrame(() => {
      node.style.transform = `translate(${flyer.dx}px, ${flyer.dy}px) scale(.5)`;
      node.style.opacity = '0.1';
    });

    return () => cancelAnimationFrame(frame);
  }, [flyer]);

  const days = useMemo(() => weekDays(weekStart), [weekStart]);
  const hours = useMemo(() => hoursRange(week.events), [week.events]);
  const index = useMemo(() => indexEvents(week.events), [week.events]);
  const marked = useMemo(() => daysWithEvents(week.events), [week.events]);
  const todayIndex = useMemo(() => days.findIndex((day) => isSameDate(day, now)), [days, now]);

  /**
   * Скільки відсотків видимої доби вже минуло. Це висота заповненої частини
   * колонки «сьогодні»: сітка починається з `hours[0]` і триває `hours.length` годин.
   */
  const dayProgress = useMemo(() => {
    if (todayIndex < 0 || hours.length === 0) {
      return 0;
    }

    const passed = now.getHours() + (now.getMinutes() / 60) - hours[0];

    return Math.min(100, Math.max(0, (passed / hours.length) * 100));
  }, [todayIndex, hours, now]);

  /**
   * Сітка перемонтовується на кожен новий тиждень (ключі клітинок містять дату),
   * тож каскад програється і при гортанні — а разом з ним щоразу заново
   * доростає шкала доби.
   */
  useEffect(() => {
    if (loading || !week.connected) {
      return undefined;
    }

    setEntered(false);

    const delay = CASCADE_BASE_MS + (Math.max(0, todayIndex) * CASCADE_STEP_MS) + CASCADE_TAIL_MS;
    const timer = setTimeout(() => setEntered(true), delay);

    return () => clearTimeout(timer);
  }, [loading, weekStart, week.connected, todayIndex]);

  const goWeek = (offset) => {
    const next = offset === 0 ? weekStartOf(new Date()) : addWeeks(weekStart, offset);

    setWeekStart(next);
    setSelectedDay(offset === 0 ? new Date() : next);
  };

  const eventsAt = (day, hour) => index[`${dayKeyOf(day)}|${hour}`] || [];

  const openCreate = (day, hour) => {
    const base = day || selectedDay;
    // Без години (кнопка «＋ Подія») беремо наступну повну годину, але не пізніше
    // 23:00 — інакше о 23:xx вийшов би час «24:00».
    const startHour = hour === undefined ? Math.min(23, new Date().getHours() + 1) : hour;
    const start = new Date(base.getFullYear(), base.getMonth(), base.getDate(), startHour, 0, 0);
    const end = addHours(start, 1);

    setModalError('');
    setModal({
      mode: 'create',
      event: {
        start_at: format(start, API_DATETIME),
        end_at: format(end, API_DATETIME),
        repeat: 'none',
        attendees: [],
        reminder_minutes: null,
      },
    });
  };

  const openEvent = (item) => {
    setModalError('');
    setModal({ mode: item.editable ? 'edit' : 'view', event: item });
  };

  const closeModal = () => {
    setModal(null);
    setModalError('');
  };

  /**
   * Помилку показуємо там, де людина її побачить: у формі, якщо вона ще
   * відкрита, і тостом, якщо форму встигли закрити (Escape під час збереження).
   */
  const reportModalError = (requestError, fallback) => {
    const text = readErrorText(requestError, fallback);

    if (modal) {
      setModalError(text);
    } else {
      showToast(text, 'error');
    }
  };

  const handleSave = (payload) => {
    const isEdit = modal?.mode === 'edit';
    const request = isEdit
      ? calendarEventUpdate({ token, id: modal.event.id, event: payload })
      : calendarEventCreate({ token, event: payload });

    // Знімаємо позицію форми ДО закриття — саме з неї полетить плашка події.
    const fromRect = document.querySelector('.cal-modal__box')?.getBoundingClientRect() || null;

    setSaving(true);
    setModalError('');

    request
      .then((response) => {
        if (response?.data?.info?.status) {
          const created = response.data.data?.event || null;

          closeModal();
          load(weekStart, { silent: true });
          launchFlight(created, fromRect);
          showToast(isEdit ? 'Зміни збережено' : 'Подію додано');
        } else {
          setModalError(response?.data?.info?.message || 'Не вдалося зберегти подію.');
        }
      })
      .catch((requestError) => reportModalError(requestError, 'Не вдалося зберегти подію.'))
      .finally(() => setSaving(false));
  };

  const handleDelete = () => {
    if (!modal?.event?.id) {
      return;
    }

    setSaving(true);
    setModalError('');

    calendarEventDelete({ token, id: modal.event.id })
      .then((response) => {
        if (response?.data?.info?.status) {
          closeModal();
          load(weekStart, { silent: true });
          showToast('Подію видалено');
        } else {
          setModalError(response?.data?.info?.message || 'Не вдалося видалити подію.');
        }
      })
      .catch((requestError) => reportModalError(requestError, 'Не вдалося видалити подію.'))
      .finally(() => setSaving(false));
  };

  const renderChip = (item) => (
    <button
      type="button"
      key={item.key}
      className={[
        'cal-chip',
        `cal-chip--${TYPE_MODIFIER[item.type] || 'own'}`,
        item.id && item.id === highlightId ? 'is-new' : '',
      ].filter(Boolean).join(' ')}
      onClick={() => openEvent(item)}
    >
      <b>{item.title}</b>
      <i>{item.editable ? `${item.start} – ${item.end}` : (item.teacher || item.type_label)}</i>
      <span className="cal-chip__mark">{item.editable ? '✎' : '🔒'}</span>
    </button>
  );

  const renderGrid = () => (
    <div className="cal-grid-wrap">
      <div
        className="cal-grid"
        style={{ gridTemplateColumns: '58px repeat(7, minmax(96px, 1fr))' }}
        onMouseLeave={() => setHover({ day: null, hour: null })}
      >
        {/*
          Шкала доби в колонці «сьогодні»: заповнена частина = час, що вже минув.
          Абсолютний елемент, а не grid-item — інакше він зайняв би комірку і
          решта клітинок перестрибнула б колонку при auto-placement.
        */}
        {todayIndex >= 0 && (
          <div className="cal-grid__today" style={{ '--cal-day': todayIndex }} aria-hidden="true">
            <div className="cal-grid__today-fill" style={{ height: `${entered ? dayProgress : 0}%` }}>
              <span className="cal-grid__today-flow"></span>
              <span className="cal-grid__today-head"></span>
            </div>
          </div>
        )}

        <div className="cal-grid__corner"></div>
        {days.map((day, dayIndex) => {
          const key = dayKeyOf(day);

          return (
            <div
              key={key}
              data-day={key}
              style={{ '--cal-col': dayIndex }}
              className={[
                'cal-grid__head',
                isWeekend(day) ? 'is-weekend' : '',
                isSameDate(day, now) ? 'is-today' : '',
                hover.day === key ? 'is-hl' : '',
              ].filter(Boolean).join(' ')}
            >
              <span>{shortWeekday(day)}</span>
              <b>{format(day, 'd')}</b>
            </div>
          );
        })}

        {hours.map((hour) => (
          <React.Fragment key={hour}>
            <div className={`cal-grid__time ${hover.hour === hour ? 'is-hl' : ''}`}>
              {`${String(hour).padStart(2, '0')}:00`}
            </div>

            {days.map((day, dayIndex) => {
              const key = dayKeyOf(day);
              const items = eventsAt(day, hour);
              const isEmpty = items.length === 0;

              return (
                <div
                  key={`${key}-${hour}`}
                  data-cell={`${key}|${hour}`}
                  style={{ '--cal-col': dayIndex }}
                  className={[
                    'cal-grid__cell',
                    isWeekend(day) ? 'is-weekend' : '',
                    hover.day === key ? 'is-hl-col' : '',
                    hover.hour === hour ? 'is-hl-row' : '',
                  ].filter(Boolean).join(' ')}
                  // Порожня клітинка — повноцінна кнопка «створити на цю годину»,
                  // зокрема з клавіатури. Клітинка з подіями вже містить кнопки-чіпи,
                  // тож у таб-порядок її не додаємо.
                  role={isEmpty ? 'button' : 'presentation'}
                  tabIndex={isEmpty ? 0 : undefined}
                  aria-label={isEmpty ? `Додати подію ${key} о ${hour}:00` : undefined}
                  onMouseEnter={() => setHover({ day: key, hour })}
                  onFocus={() => setHover({ day: key, hour })}
                  onClick={(domEvent) => {
                    if (domEvent.target === domEvent.currentTarget) {
                      openCreate(day, hour);
                    }
                  }}
                  onKeyDown={(domEvent) => {
                    if (isEmpty && (domEvent.key === 'Enter' || domEvent.key === ' ')) {
                      domEvent.preventDefault();
                      openCreate(day, hour);
                    }
                  }}
                >
                  {items.map(renderChip)}

                  {isEmpty && (
                    <span className="cal-grid__add" aria-hidden="true">
                      <i>＋</i>
                    </span>
                  )}
                </div>
              );
            })}
          </React.Fragment>
        ))}
      </div>
    </div>
  );

  const renderDay = () => {
    const dayItems = hours.map((hour) => ({ hour, items: eventsAt(selectedDay, hour) }));
    const hasAny = dayItems.some((row) => row.items.length > 0);

    return (
      <>
        <div className="cal-days">
          {days.map((day) => (
            <button
              type="button"
              key={dayKeyOf(day)}
              data-day={dayKeyOf(day)}
              className={`cal-days__item ${isSameDate(day, selectedDay) ? 'is-active' : ''}`}
              onClick={() => setSelectedDay(day)}
            >
              <span>{shortWeekday(day)}</span>
              <b>{format(day, 'd')}</b>
              {marked[dayKeyOf(day)] && <i className="cal-days__mark"></i>}
            </button>
          ))}
        </div>

        <div className="cal-day-label">{dayLabel(selectedDay)}</div>

        {!hasAny && (
          <div className="cal-plug cal-plug--day">
            <div className="cal-plug__title">На цей день подій немає</div>
            <p className="cal-plug__text">Можна додати свою подію — вона потрапить і у ваш Google-календар.</p>
            <button type="button" className="cal-btn cal-btn--main" onClick={() => openCreate(selectedDay)}>
              ＋ Додати подію
            </button>
          </div>
        )}

        {hasAny && (
          <div className="cal-list">
            {dayItems.map((row) => (
              <div className="cal-list__row" key={row.hour}>
                <div className="cal-list__time">{`${String(row.hour).padStart(2, '0')}:00`}</div>
                <div className="cal-list__body">
                  {row.items.length > 0
                    ? row.items.map(renderChip)
                    : (
                      <button
                        type="button"
                        className="cal-list__slot"
                        onClick={() => openCreate(selectedDay, row.hour)}
                      >
                        ＋ додати
                      </button>
                    )}
                </div>
              </div>
            ))}
          </div>
        )}
      </>
    );
  };

  const renderPlug = () => (
    <div className="cal-plug">
      <div className="cal-plug__icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
          <rect x="3" y="5" width="18" height="16" rx="3" />
          <path d="M8 3v4M16 3v4M3 10h18" />
        </svg>
      </div>

      {week.available && week.enabled ? (
        <>
          <div className="cal-plug__title">Підключіть Google Calendar</div>
          <p className="cal-plug__text">
            Календар покаже ваші заняття, пробні й відпрацювання і дозволить додавати власні події —
            усе синхронізується з Google-календарем на телефоні.
          </p>
          <button
            type="button"
            className="cal-btn cal-btn--main"
            onClick={() => {
              requestClose();
              navigate('/settings');
            }}
          >
            Підключити в налаштуваннях
          </button>
        </>
      ) : (
        <>
          <div className="cal-plug__title">Календар поки недоступний</div>
          <p className="cal-plug__text">
            Синхронізацію з Google Calendar для вашого акаунта ще не увімкнено. Напишіть менеджеру —
            і ми відкриємо доступ.
          </p>
        </>
      )}
    </div>
  );

  const renderBody = () => {
    if (loading) {
      return (
        <div className="cal-skeleton">
          <span style={{ height: '44px' }} />
          <span style={{ height: '56px' }} />
          <span style={{ height: '56px', width: '88%' }} />
          <span style={{ height: '56px', width: '74%' }} />
        </div>
      );
    }

    if (error) {
      return (
        <div className="cal-plug">
          <p className="cal-note cal-note--err" role="alert">{error}</p>
          <button type="button" className="cal-btn cal-btn--ghost" onClick={() => load(weekStart)}>
            Спробувати ще раз
          </button>
        </div>
      );
    }

    if (!week.connected) {
      return renderPlug();
    }

    return isDesktop ? renderGrid() : renderDay();
  };

  // Портал у <body>: панель рендериться з шапки кабінету, а шапка — position: fixed
  // із z-index: 10, тобто ВЛАСНИЙ stacking context. Без порталу весь оверлей живе
  // всередині z=10, і будь-який елемент сторінки з більшим z-index малюється поверх
  // календаря: саме так сторінковий заголовок (.head, sticky, z-index: 100)
  // прорізав панель білою смугою «Налаштування».
  return createPortal(
    <div className={`cal-panel ${closing ? 'is-closing' : ''}`}>
      <div className="cal-panel__overlay" onClick={requestClose} role="presentation"></div>

      <section
        className="cal-panel__box"
        ref={boxRef}
        role="dialog"
        aria-modal="true"
        aria-label="Мій календар"
      >
        <header className="cal-panel__head">
          <div className="cal-panel__title">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
              <rect x="3" y="5" width="18" height="16" rx="3" />
              <path d="M8 3v4M16 3v4M3 10h18" />
            </svg>
            Мій календар
          </div>

          {week.connected && !loading && !error && (
            <button type="button" className="cal-btn cal-btn--main cal-btn--sm" onClick={() => openCreate(selectedDay)}>
              ＋ Подія
            </button>
          )}

          <button type="button" className="cal-icon-btn" aria-label="Закрити календар" onClick={requestClose}>✕</button>
        </header>

        <div className="cal-panel__week">
          <button
            type="button"
            key={`prev-${navPulse?.dir === 'prev' ? navPulse.id : 0}`}
            data-nav="prev"
            className={`cal-nav ${navPulse?.dir === 'prev' ? 'is-hit' : ''}`}
            aria-label="Попередній тиждень"
            onClick={() => goWeek(-1)}
          >
            ‹
          </button>
          <div className="cal-panel__label">{weekLabel(weekStart)}</div>
          <button
            type="button"
            key={`next-${navPulse?.dir === 'next' ? navPulse.id : 0}`}
            data-nav="next"
            className={`cal-nav ${navPulse?.dir === 'next' ? 'is-hit' : ''}`}
            aria-label="Наступний тиждень"
            onClick={() => goWeek(1)}
          >
            ›
          </button>
          <button type="button" className="cal-btn cal-btn--ghost cal-btn--sm" onClick={() => goWeek(0)}>Сьогодні</button>
        </div>

        <div className="cal-panel__body">{renderBody()}</div>

        {week.connected && !loading && !error && (
          <footer className="cal-legend">
            <span><i className="cal-legend__sq cal-legend__sq--lesson" />Заняття</span>
            <span><i className="cal-legend__sq cal-legend__sq--trial" />Пробне</span>
            <span><i className="cal-legend__sq cal-legend__sq--catchup" />Відпрацювання</span>
            <span><i className="cal-legend__sq cal-legend__sq--own" />Моя подія</span>
            <span>🔒 з розкладу — не редагується</span>
          </footer>
        )}
      </section>

      {flyer && (
        <div
          key={flyer.id}
          ref={flyerRef}
          className="cal-flyer"
          style={{ left: `${flyer.left}px`, top: `${flyer.top}px` }}
          aria-hidden="true"
        >
          {flyer.label}
        </div>
      )}

      {toast && (
        <div className={`cal-toast cal-toast--${toast.tone}`} role="status" key={toast.id}>
          <span className="cal-toast__mark">{toast.tone === 'error' ? '!' : '✓'}</span>
          {toast.text}
        </div>
      )}

      {modal && (
        <CalendarEventModal
          mode={modal.mode}
          event={modal.event}
          reminderDefault={week.reminder_default}
          saving={saving}
          error={modalError}
          onSave={handleSave}
          onDelete={handleDelete}
          onClose={closeModal}
        />
      )}
    </div>,
    document.body
  );
};

CalendarPanel.propTypes = {
  token: PropTypes.string,
  origin: PropTypes.shape({ x: PropTypes.number, y: PropTypes.number }),
  onClose: PropTypes.func.isRequired,
};

CalendarPanel.defaultProps = {
  token: '',
  origin: null,
};

export default CalendarPanel;
